import React, { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import ContextSwitcher from './ContextSwitcher';
import GoToPalette from './GoToPalette';
import type { DestGroup, Destination } from './GoToPalette';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Building2,
  CreditCard, LogOut, Bot, Settings,
  Users, X, Store, Search,
  Sparkles, Target, Boxes, MoreHorizontal, Check, ListChecks,
  Send, FileText, HelpCircle, Wrench, Award, Scale, Library, Wand2, Network, CalendarDays, LayoutTemplate,
  Home, FolderKanban, Database, Clock, Menu, BarChart3, Plus, Plug, TrendingUp,
} from 'lucide-react';
import clsx from 'clsx';
import { OrbitChatDrawer } from '../orbit/dossier/OrbitChatDrawer';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { getClientProfile, getCitationSettings, logoutUser, requestAgentBridgeUrl } from '../services/api';
import { listClientProblems } from '../orbit/lib/scopeApi';
import { aaiApi } from '../services/aaiApi';
import { billingApi, type SeatSummary } from '../services/billingApi';
import { useParticipant } from '../hooks/useParticipant';
import { usePermissions } from '../hooks/usePermissions';
import WorkspaceSettingsModal from './WorkspaceSettingsModal';
import { homeFor, mayOpen } from '../lib/roleRoutes';
import { NotificationBell } from './NotificationBell';
import AiCompanion from './companion/AiCompanion';

interface NavItem {
  path: string;
  label: string;
  icon: React.ElementType;
  disabled?: boolean;       // greyed, non-navigable (a "coming soon" surface)
  count?: number;           // optional badge (e.g. number of problems)
  section?: string;         // renders a small uppercase group header above this item
  /** G3: the permission this surface needs. Undefined means everyone in the
   *  role's portal sees it (a dashboard, your own readiness, help). Removing
   *  the grant from a role in the roles page removes the item on next load,
   *  with no deploy. */
  perm?: string;
}

// G3. Drop the items the caller has no grant for, then re-flow the group
// headers. A header lives on the FIRST item of its group, so filtering without
// this step silently deletes the header along with a hidden first item and the
// rest of that group reads as belonging to the group above it.
const withPermissions = (items: NavItem[], can: (p?: string | null) => boolean): NavItem[] => {
  const kept: NavItem[] = [];
  let group: string | undefined;
  let groupOpen = false;
  items.forEach((item) => {
    if (item.section) { group = item.section; groupOpen = false; }
    if (!can(item.perm)) return;
    if (group && !groupOpen) { kept.push({ ...item, section: group }); groupOpen = true; return; }
    kept.push({ ...item, section: undefined });
  });
  return kept;
};

interface LayoutProps {
  onShowChat: () => void;
  clientId: string | null;
  /** Stay on the current address: the Page not found screen, which must not be
   *  bounced to a dashboard like a page this role cannot open. */
  keepPath?: boolean;
  children: React.ReactNode;
}

/*
 * The account mark.
 *
 * The employee shell draws it as a dark rounded tile carrying initials, which
 * is the same mark the topbar uses for the company: on a strip where the two
 * sit a row apart, giving them one shape says they are both identities and
 * lets position say which is which. Every other role keeps the yellow disc
 * that the brand kit specifies for the admin shells.
 */
const initialsOf = (name: string): string => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

/*
 * The line under the name in the account card. The design puts the person's
 * role there, not their address: an email is already in the menu this card
 * opens, and repeating it spends the one quiet line on something the reader
 * cannot act on.
 */
const SCOPING_ROLES = ['OWNER', 'AI_CHAMPION', 'DEPARTMENT_HEAD', 'AI_PARTICIPANT'];

const ROLE_LABEL: Record<string, string> = {
  AI_PARTICIPANT: 'Employee',
  DEPARTMENT_HEAD: 'Department head',
  AI_CHAMPION: 'AI champion',
  OWNER: 'Owner',
  TRAINER: 'Trainer',
  EXPERT: 'Expert',
};

const AccountMark: React.FC<{ participant: boolean; name: string }> = ({ participant, name }) => {
  if (!participant) {
    return (
      <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#ffea00] text-xs font-bold text-black">
        {name.charAt(0).toUpperCase()}
      </div>
    );
  }
  return (
    <div className="grid h-[34px] w-[34px] shrink-0 place-items-center rounded-tile bg-ink text-[12px] font-bold text-white">
      {initialsOf(name)}
    </div>
  );
};

const CompanyMark: React.FC<{ to: boolean; name: string }> = ({ to, name }) => {
  const face = (
    <>
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-tile bg-ink text-[12px] font-bold text-white">
        {name.charAt(0).toUpperCase()}
      </span>
      {/* On a phone the mark is the whole control: the company name is one row
          above it in the shell already, and repeating it here costs the row its
          breathing space. */}
      <strong className="hidden truncate text-[13px] font-bold tracking-[-0.005em] text-ink sm:block">{name}</strong>
    </>
  );
  if (!to) return <span className="flex min-w-0 items-center gap-3">{face}</span>;
  return (
    <Link to="/company-context" title={`${name} — company context`} className="flex min-w-0 items-center gap-3">
      {face}
    </Link>
  );
};



const planDollars = (cents: number) => `$${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const planTitle = (plan: SeatSummary): string => {
  if (plan.plan === 'workspace') return 'Team plan';
  if (plan.plan === 'legacy') return `${plan.plan_name || 'Legacy'} plan`;
  return 'Free plan';
};

const planDetail = (plan: SeatSummary): string => {
  const seats = plan.seats ?? 1;
  if (plan.plan === 'free') return 'Just you · $0 a month';
  return `${seats} ${seats === 1 ? 'seat' : 'seats'} · ${planDollars(plan.monthly_cents ?? 0)} a month`;
};

const Layout: React.FC<LayoutProps> = ({ onShowChat, clientId, keepPath = false, children }) => {
  const [citationsEnabled, setCitationsEnabled] = useState(false);
  const [goToOpen, setGoToOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  // undefined: not read yet; null: could not be read.
  const [ownerPlan, setOwnerPlan] = useState<SeatSummary | null | undefined>(undefined);
  const [navOpen, setNavOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsSection, setSettingsSection] = useState('general');
  const profileMenuRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { impersonationState, endImpersonation } = useImpersonation();

  // The phone drawer closes once a destination is chosen.
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!navOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setNavOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navOpen]);

  // Navigating away (e.g. the paywall's "Upgrade" -> billing) must close the
  // settings modal. It's a full-screen overlay rendered outside the router, so
  // otherwise it stays mounted on top of the new page — hiding it, AND keeping
  // its contents (plan tier, team seats) stale because they never remount to
  // refetch. Closing on route change fixes both: billing becomes visible, and
  // reopening Settings remounts the tabs and re-reads the (now upgraded) plan.
  useEffect(() => {
    setSettingsOpen(false);
  }, [location.pathname]);

  // /settings?tab= lands here with the section in router state: opening it
  // before the redirect lost it to the route-change close above.
  useEffect(() => {
    const section = (location.state as { openSettings?: string } | null)?.openSettings;
    if (!section) return;
    setSettingsSection(section);
    setSettingsOpen(true);
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [location.state, location.pathname, location.search, navigate]);
  const isImpersonating = impersonationState.isImpersonating;

  // AAI participants and department heads get a stripped-down app — no
  // business/builds/team surfaces. The AI Adoption row is hidden from every
  // role for now (Sept 2026): /aai and /ai-adoption still resolve and every
  // link into them still works, so the programme keeps running, it just does
  // not compete with Projects for the main flow.
  // Drives both the nav set below and a guard that keeps them out of hidden
  // pages by URL.
  const { isParticipant, isDepartmentHead, role: aaiRole, ready: roleReady } = useParticipant();
  const { can } = usePermissions();
  // Onboarding is a one-time task, so it retires once it's done. H1 moved it
  // off the sidebar entirely: it is a tab on My Readiness while unfinished (see
  // MyGrowthHub), which applies the same rule in the place the person already
  // goes to see where they stand.
  const isChampion = aaiRole === 'AI_CHAMPION';
  const isOwner = aaiRole === 'OWNER';

  // The Owner sees their plan at a glance in the profile menu. It is read as
  // soon as the role is known, so the menu opens with it already there, and
  // read again on each open so seats changed since then show.
  const loadOwnerPlan = useCallback(() => {
    billingApi.getSeats().then(({ data }) => setOwnerPlan(data)).catch(() => setOwnerPlan(null));
  }, []);
  useEffect(() => {
    if (isOwner) loadOwnerPlan();
  }, [isOwner, loadOwnerPlan]);
  useEffect(() => {
    if (profileMenuOpen && isOwner) loadOwnerPlan();
  }, [profileMenuOpen, isOwner, loadOwnerPlan]);
  // Trainer — external delivery specialist. Confined to the AI-Adoption cockpit
  // (curriculum + delivery + departments), no workspace/build/governance surfaces.
  const isTrainer = aaiRole === 'TRAINER';
  // Expert — the engineer who reviews PRDs before builds (brndsh-side or
  // internal). Confined to the review queue + company dashboard.
  const isExpert = aaiRole === 'EXPERT';
  // Executives and team leads hold room grants and nothing else: they were given
  // a workspace role so they could answer Orbit's questions about the company.
  // Without a nav of their own they fell through to the plain client's five
  // rows, three of which (My Business, Projects, My Tools) they may not open.
  const isRoomOnly = aaiRole === 'EXECUTIVE' || aaiRole === 'TEAM_LEAD';
  /*
   * What this person may open. The list lives in lib/roleRoutes, one entry per
   * address the role's own UI offers it, because the sidebar deciding what is
   * SHOWN was never the same as deciding what can be REACHED.
   */
  const restrictedHome = homeFor(aaiRole);

  // The global Orbit entry point — opens the drawer on a fresh problem.
  const isMyBusiness = location.pathname.startsWith('/my-business');
  
  const openScope = React.useCallback(() => {
    if (isMyBusiness) {
      window.dispatchEvent(new CustomEvent('orbit:open', { detail: { route: 'company', fresh: true, context: 'company' } }));
    } else {
      window.dispatchEvent(new CustomEvent('orbit:open', { detail: { route: 'problem', fresh: true, context: 'problem' } }));
    }
  }, [isMyBusiness]);

  const [profile, setProfile] = useState<any>(null);
  const userRole = localStorage.getItem('user_role');
  const userEmail = localStorage.getItem('user_email') || '';
  // Reactive so an in-app profile edit (Settings → General) updates the sidebar
  // immediately, without a page reload.
  const [userName, setUserName] = useState<string>(
    localStorage.getItem('user_name') || userEmail.split('@')[0] || 'User'
  );
  useEffect(() => {
    const refresh = () =>
      setUserName(localStorage.getItem('user_name') || userEmail.split('@')[0] || 'User');
    window.addEventListener('user-profile-updated', refresh);
    window.addEventListener('storage', refresh); // other tabs
    return () => {
      window.removeEventListener('user-profile-updated', refresh);
      window.removeEventListener('storage', refresh);
    };
  }, [userEmail]);
  const clientName = localStorage.getItem('client_name') || profile?.prompt_display_name || profile?.name || 'Your workspace';

  // Provider tenant: a provider member flagged admin and/or expert reaches the
  // builder from a sidebar link — a crossing that opens the builder in a NEW TAB
  // (no builder login). A builder-admin lands on the admin view; an expert-only
  // member lands on their expert view. Gated by the flags, so no other client
  // ever sees it. (Generic — any provider, not just AA.)
  const isBuilderAdmin = localStorage.getItem('is_builder_admin') === '1';
  const isBuilderExpert = localStorage.getItem('is_expert') === '1';
  const ownsProvider = Boolean(localStorage.getItem('provider_id'));
  const isCompanyUser = userRole === 'client' || userRole === 'client_admin';
  const scopingRole = !aaiRole || SCOPING_ROLES.includes(aaiRole);
  const canStartScoping = isCompanyUser && roleReady && scopingRole && can('aai_request_something');
  const [openingBuilder, setOpeningBuilder] = useState(false);
  const openBuilder = async () => {
    setOpeningBuilder(true);
    try {
      // Admin has both views (with a toggle on the builder); an expert-only
      // member goes straight to their expert dashboard; an unflagged provider
      // participant lands on the workspace's customer view (server default).
      // Every builder admin lands on /admin; another provider's admin sees it
      // limited to their own company and clients.
      let dest: string | undefined;
      if (isBuilderAdmin) {
        dest = '/admin';
      } else if (isBuilderExpert) {
        dest = '/expert';
      }
      const res: any = await requestAgentBridgeUrl(dest);
      if (res?.url) window.open(res.url, '_blank');
    } catch {
      /* keep the sidebar quiet; the builder just doesn't open */
    } finally {
      setOpeningBuilder(false);
    }
  };

  // Fetch profile data
  const fetchProfile = async () => {
    try {
      const cid = localStorage.getItem('client_id');
      if (cid) {
        const data = await getClientProfile(cid);
        setProfile(data);
      }
    } catch (error) {
      console.error('Failed to load profile:', error);
    }
  };

  // Check citation status
  const checkCitationStatus = async () => {
    try {
      const cid = localStorage.getItem('client_id');
      if (cid) {
        const data = await getCitationSettings(cid);
        if (data.success && data.citations_enabled === true) {
          setCitationsEnabled(true);
          localStorage.setItem('citations_enabled', 'true');
        } else {
          setCitationsEnabled(false);
          localStorage.setItem('citations_enabled', 'false');
        }
      }
    } catch (error) {
      console.error('Error checking citation status:', error);
    }
  };

  // Initialize citations from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('citations_enabled');
    if (stored === 'true') setCitationsEnabled(true);
  }, []);

  useEffect(() => {
    fetchProfile();
    checkCitationStatus();

    const handleCitationSettingsChange = () => checkCitationStatus();
    const handleProfileRefresh = () => fetchProfile();

    window.addEventListener('citation-settings-changed', handleCitationSettingsChange);
    window.addEventListener('profile-data-changed', handleProfileRefresh);
    return () => {
      window.removeEventListener('citation-settings-changed', handleCitationSettingsChange);
      window.removeEventListener('profile-data-changed', handleProfileRefresh);
    };
  }, []);

  // (Profile is fetched on mount + on the `profile-data-changed` event above.
  // The old per-navigation refetch was pure overhead — removed.)


  useEffect(() => {
    if (!profileMenuOpen) return;
    const onDoc = (event: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setProfileMenuOpen(false);
      }
    };
    const onEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setProfileMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onEsc);
    };
  }, [profileMenuOpen]);

  /* Does this company have a department yet? Only the roles that could be
     offered Company Context ask, so nobody else pays for a read they cannot
     use. It re-asks when a department is added, which is the one moment the
     answer changes from no to yes. */
  const [hasDepartment, setHasDepartment] = useState<boolean | null>(null);
  const wantsContext = isOwner || isDepartmentHead || isRoomOnly;
  useEffect(() => {
    if (!wantsContext) { setHasDepartment(false); return undefined; }
    let cancelled = false;
    const refresh = async () => {
      try {
        const res = await aaiApi.listDepartments(true);
        if (!cancelled) setHasDepartment((res.data.departments || []).length > 0);
      } catch {
        // A read that failed is not an answer. Leave it unknown rather than
        // deciding the company has no departments because the network did not
        // reply, which would hide the screen from somebody who has them.
      }
    };
    refresh();
    const onChange = () => refresh();
    window.addEventListener('aai:departments-changed', onChange);
    return () => {
      cancelled = true;
      window.removeEventListener('aai:departments-changed', onChange);
    };
  }, [wantsContext, clientId]);

  // Live problem count for the Problems nav badge. Refresh on mount, on
  // identity change, and on the Orbit events the Problems list listens to
  // (create/after-turn/changed) — those already track creates/deletes, so we
  // DON'T refetch on every navigation (that was part of the request flood).
  const [problemCount, setProblemCount] = useState<number | null>(null);
  useEffect(() => {
    const cid = clientId || localStorage.getItem('client_id') || '';
    if (!cid || !(userRole === 'client' || userRole === 'client_admin')) return;
    let cancelled = false;
    const refresh = async () => {
      try {
        const probs = await listClientProblems(cid);
        if (!cancelled) setProblemCount(probs.length);
      } catch { /* leave prior count */ }
    };
    refresh();
    const onChange = () => refresh();
    window.addEventListener('orbit:problem-created', onChange);
    window.addEventListener('orbit:after-turn', onChange);
    window.addEventListener('orbit:problems-changed', onChange);
    return () => {
      cancelled = true;
      window.removeEventListener('orbit:problem-created', onChange);
      window.removeEventListener('orbit:after-turn', onChange);
      window.removeEventListener('orbit:problems-changed', onChange);
    };
  }, [clientId, userRole]);

  const isAaiUser = !!aaiRole || isParticipant || isDepartmentHead;
  /*
   * Who has a Company Context screen at all.
   *
   * The Owner is the company, and a department head reads their own department
   * through it. Not the employee: their own questions reach them on Home, and
   * the company's record is not theirs. Not the AI Champion either, who runs
   * the programme rather than holding the record.
   *
   * And not before there is a company to read. Company Context is departments,
   * the people in them, and what each one works on; with none of that it is a
   * set of empty panels that says the product is broken rather than that the
   * company has not been described yet. A brand new Owner sets up departments
   * first, under Settings, and the row appears when the first one lands.
   *
   * `hasDepartment` starts null and the door stays shut until the answer
   * arrives, because offering a door and then taking it away is worse than
   * showing it a moment late.
   *
   * Every entry point below asks this one question, so a role can never be
   * offered a door the route guard will shut.
   */
  /* The Owner always: Company Context is where the Orbit Room and the journey
     live, and the journey is how departments come to exist, so waiting for one
     before showing it shut the Owner out of the step that makes them. */
  const hasCompanyContext = isOwner || ((isDepartmentHead || isRoomOnly) && hasDepartment === true);
  /* The Owner's and the department head's Context row, Activity button and
     palette entries are off for now: the Orbit Room moved to Home and the
     Company tools and Activity tabs are commented out. The Owner's Controls is
     reached from Home. */
  const contextInNav = hasCompanyContext && !isOwner && !isDepartmentHead;
  // A head's context screen is their department, and says so everywhere it is
  // named: the sidebar row, the breadcrumb, and the ⌘K palette.
  let contextLabel = 'Company Context';
  if (isDepartmentHead) contextLabel = 'Department context';
  if (isOwner) contextLabel = 'Connections & access';

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'k' || !(event.metaKey || event.ctrlKey)) return;
      event.preventDefault();
      setGoToOpen((open) => !open);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Live "awaiting your approval" count for the DASHBOARD nav badge (F2 P0:
  // use-case requests + skill requests waiting on the caller). Distinct from the
  // notifications unread count. Attached to the caller's dashboard nav item below.
  // The Owner's decisions live on Home now, so the badge sits on Home for them.
  const approvalsPath = isOwner || isDepartmentHead || isChampion ? '/dashboard' : null;
  const [approvalsCount, setApprovalsCount] = useState<number | null>(null);
  useEffect(() => {
    if (!approvalsPath) return;
    let cancelled = false;
    const refresh = async () => {
      try {
        const res = await aaiApi.approvalsSummary(true);
        if (!cancelled) setApprovalsCount(res.data.count);
      } catch { /* leave prior count */ }
    };
    refresh();
    const onChange = () => refresh();
    window.addEventListener('aai:approvals-changed', onChange);
    const tick = window.setInterval(refresh, 60000);
    return () => {
      cancelled = true;
      window.removeEventListener('aai:approvals-changed', onChange);
      window.clearInterval(tick);
    };
  }, [approvalsPath, location.pathname]);

  /*
   * What this person reaches through something other than their role.
   *
   * An employee flagged as an expert, and an employee in a department marked
   * as one that builds, both get a row into /projects that the role itself
   * never grants. The allow-list is keyed on role alone and cannot see either,
   * so the shell tells it what it is offering: otherwise the menu shows a row
   * that bounces the moment it is clicked.
   */
  const grantedRoutes = useMemo(() => {
    const out: string[] = [];
    if (isBuilderExpert || can('aai_build_directly')) out.push('/projects');
    return out;
  }, [isBuilderExpert, can]);

  // Keep restricted roles inside their allowed surfaces — if they land on a
  // hidden page (by old link/bookmark), send them to their dashboard. Waits for
  // the role to resolve so non-restricted users are never bounced.
  useEffect(() => {
    if (keepPath || !roleReady || !aaiRole) return;
    if (mayOpen(aaiRole, location.pathname, grantedRoutes)) return;
    navigate(restrictedHome, { replace: true });
  }, [keepPath, roleReady, aaiRole, location.pathname, restrictedHome, navigate, grantedRoutes]);

  // Champion/Owner keep the full Workspace group (staging's format) — Dashboard
  // is a real destination for them again, so no bounce to /aai.

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');

  // Topbar (imported design): the role kicker + the current screen's title.

  const getBaseNavigationItems = (): NavItem[] => {
    // Super admin navigation items
    if (userRole === 'super') {
      return [
        { path: '/consultant/dashboard', icon: Users, label: 'Clients' },
        { path: '/super/plan-management', icon: CreditCard, label: 'Plans & Payments' },
        { path: '/super/role-management', icon: Settings, label: 'Roles' },
        { path: '/super/consultants', icon: Users, label: 'Consultants' },
      ];
    }

    // Consultant navigation items
    if (userRole === 'consultant') {
      return [
        { path: '/consultant/dashboard', icon: Users, label: 'Clients' },
      ];
    }


    // For client and client_admin roles.
    //
    // Orbit no longer has its own nav item — the dossier surface lives
    // inside `/my-business` (Phase 3 IA consolidation). First-time users
    // hit /orbit/intake full-screen for cold-start; once their L1 dossier
    // exists, the intake page silently redirects them back to My Business
    // where the full Dossier / Scope / Routing surface lives.
    // IA: Problems is the plural scoping pipeline; "Builds" (was "Agents")
    // is the deliverables home (agents + skills + documents). Marketplace
    // stays pinned below the nav.

    // Per-role nav mirrors the imported CoE Platform design EXACTLY — each role
    // shows only the tabs the design specifies for it, nothing more. Every AAI
    // role (participant / dept-head / champion / owner / trainer / expert) is a
    // self-contained portal with no core Workspace group; product surfaces that
    // aren't in a role's design set stay reachable by direct URL, just not nav.
    // AI Participant — a single "AI Adoption" group, nine items in the design's
    // order. Lands on My Readiness (a private growth view, never a leaderboard).
    // Tool Library (/tools) and My Productivity (/my-productivity) are not part of
    // the design's participant set — they stay reachable by URL, not standing nav.
    // Staging's format (Usama, 2026-08-25): one AI ADOPTION section holding
    // staging's tabs PLUS the new CoE tabs. Dashboard = the role-based home
    // (RoleDashboard); My Readiness = the 5-dimension readiness graph.
    if (isParticipant) {
      // H1: was ten rows. Onboarding is a tab on My Readiness while it is
      // unfinished; Skill Builder and the Skills Marketplace are tabs on My
      // Tools; What's Allowed is a tab on Help. Every one of those addresses
      // still resolves on its own, so the CTAs that link them keep working.
      // The Platform UI Guide's employee IA: five items under one Workspace
      // group, in the guide's order. Each is a new home for a surface that
      // already existed (My Tools became Quick Wins, readiness and training
      // merged into AI Adoption, productivity became Results), and every old
      // address still resolves on its own, so links in lessons, emails and
      // bookmarks keep working.
      const items: NavItem[] = [
        { section: 'Workspace', path: '/dashboard', icon: Home, label: 'Home' },
        { path: '/my-projects', icon: FolderKanban, label: 'Projects' },
        // Documents row dropped for client users (CTO) — /documents still resolves by URL.
      ];
      // Expert-flagged members (ClientTeamTab toggle -> provider roster ->
      // /aai/me) do HITL work: their contributed client projects + the planner.
      // No perm on this one: the gate is the is_expert FLAG, not a workspace
      // role. AI_PARTICIPANT is never granted aai_view_projects, so a
      // permission check here would hide the very projects they were flagged
      // to work on.
      // I4: someone in a department marked as one that builds holds
      // aai_build_directly, which is granted by the department rather than the
      // role. They skip the request path, so they need to see the work.
      //
      // Client work used to be a row here. It is a tab on Projects now: an
      // employee's own work and the client work they are named on are both
      // projects, and a second nav row for the second kind made the sidebar
      // answer a question ("which Projects?") the page can answer better.
      // /projects still resolves for anyone holding the old address.
      if (isBuilderExpert) {
        return items;
      }
      if (can('aai_build_directly')) {
        items.push({ path: '/projects', icon: Boxes, label: 'Company projects' });
      }
      return items;
    }

    // Department Head — a team operating role, not a reduced Admin. Home is
    // the attention queue, and Open requests opens from the review panel on it.
    // Projects is the department aggregate, gated on the department rollup
    // grant. Workspace is not a nav item: it opens from a Delivery Project for
    // named members only. The old addresses (My Department, Approval Queue,
    // Use Cases) still resolve.
    if (isDepartmentHead) {
      return [
        { section: 'Workspace', path: '/dashboard', icon: Home, label: 'Home' },
        { path: '/projects', icon: FolderKanban, label: 'Projects', perm: 'aai_request_something' },
        // Documents row dropped for client users (CTO) — /documents still resolves by URL.
      ];
    }

    // Expert — the engineer gate. The PRD review queue (the workbench), the
    // company dashboard (context), and the registry/evaluator read through
    // their locked technical lens (models, data flow, human review).
    if (isExpert) {
      // H1: the vendor evaluator is a tab on Use Cases. PRD Reviews stays a row
      // of its own because it is the expert's workbench, not a reference view.
      return [
        // The work with their name on it, and the first row for a reason: it is
        // the only one that answers "what am I on". Requests below it is the
        // company's intake, which they read but do not own.
        { section: 'Workspace', path: '/my-work', icon: Boxes, label: 'My work' },
        { path: '/aai/prd-reviews', icon: ListChecks, label: 'PRD Reviews', perm: 'aai_review_prd' },
        { path: '/requests', icon: Send, label: 'Requests', perm: 'aai_request_something' },
        { path: '/aai/registry', icon: FileText, label: 'Use Cases', perm: 'aai_view_registry' },
      ];
    }

    // Trainer — external delivery specialist. The design's delivery cockpit:
    // Program (curriculum) → Delivery → Cohorts → Roster → Reusable Skills, then
    // an Adoption view for targeting. Registry & Vendor Evaluator are NOT in the
    // trainer's set (they stay reachable by URL). No workspace/build/governance.
    if (isTrainer) {
      return [
        // Home is the delivery queue: what the company has asked of the trainer,
        // the sessions ahead, the teams to target. It is the row that was
        // missing — a support request routed to the trainer had nowhere to land.
        { section: 'Workspace', path: '/dashboard', icon: Home, label: 'Home' },
        // H1: Templates is a tab on Program, because you edit a template in order to
        // change the programme, so it belongs behind the same row.
        { section: 'Delivery', path: '/aai/curriculum', icon: Sparkles, label: 'Program', perm: 'aai_manage_program' },
        { path: '/aai/delivery', icon: CalendarDays, label: 'Delivery', perm: 'aai_manage_program' },
        { path: '/aai/cohorts', icon: Network, label: 'Cohorts', perm: 'aai_manage_program' },
        { path: '/aai/departments', icon: Users, label: 'Roster' },
        { path: '/aai/skills', icon: Library, label: 'Skills & Plugins', perm: 'aai_view_marketplace' },
      ];
    }

    // The AI Champion portal (Sept 2026): Home, Portfolio, Projects under one
    // Workspace group, the Ask Orbit card and Company Context in the footer.
    // Company Dashboard became the portfolio; Use Cases folded into Projects;
    // People and My Tools moved to Settings. Open requests is reached from the
    // priorities panel on Home, which is the queue it lists in full. Every old
    // address still resolves on its own.
    if (isChampion) {
      return [
        { section: 'Workspace', path: '/dashboard', icon: Home, label: 'Home' },
        // { path: '/portfolio', icon: LayoutGrid, label: 'Portfolio', perm: 'aai_view_company_dashboard' },
        { path: '/projects', icon: FolderKanban, label: 'Projects', count: problemCount ?? undefined, perm: 'aai_view_projects' },
        { path: '/adoption', icon: TrendingUp, label: 'AI adoption', perm: 'aai_view_company_dashboard' },
        { path: '/team', icon: Users, label: 'People & departments' },
      ];
    }

    if (isOwner) {
      // The Owner UI: three rows under one Workspace group, the Ask Orbit card,
      // Company Context in the footer. The Owner IS the company, so everything
      // about the company is read in one place — Company Context carries the
      // Orbit Room, the company's tools, who decides, the activity record and
      // the platform controls. A separate Company row said the same thing
      // twice. Results moved onto Home, where the numbers sit beside the
      // decisions they came from. Dashboard became Home; My Business is reached
      // from Company Context ("Edit company facts"); My Tools moved to the
      // account menu; Use Cases folded into Projects (approved requests are the
      // registry); People & departments is the Settings panel of that name,
      // given a row here as well so the roster is one click from Home.
      //
      // Open requests opens from the decisions panel on Home rather than from a
      // row of its own: Home already IS that queue, shortened. Reports is gone
      // entirely and Documents took its place, because what people came to
      // Reports for was the files, not the counts. Both addresses still resolve.
      return [
        { section: 'Workspace', path: '/dashboard', icon: Home, label: 'Home' },
        { path: '/projects', icon: FolderKanban, label: 'Projects', count: problemCount ?? undefined, perm: 'aai_view_projects' },
        { path: '/adoption', icon: TrendingUp, label: 'AI adoption', perm: 'aai_view_company_dashboard' },
        // Documents row dropped for clients, incl. Owner (CTO) — /documents still resolves by URL.
        ...(ownsProvider ? [
          { path: '/clients', icon: Building2, label: 'Clients' },
        ] : []),
        { path: '/connectors', icon: Plug, label: 'Connectors' },
        { section: 'Company', path: '/team', icon: Users, label: 'People' },
        { path: '/usage', icon: BarChart3, label: 'Usage' },
        { path: '/billing', icon: CreditCard, label: 'Billing' },
      ];
    }

    // Room-only roles: Home, the Ask Orbit card, and Company Context in the
    // footer, which is where the Room they were invited to answer actually is.
    if (isRoomOnly) {
      return [
        { section: 'Workspace', path: '/dashboard', icon: Home, label: 'Home' },
      ];
    }

    // Default client (no AAI workspace role yet) — the standard full nav.
    const clientItems: NavItem[] = [
      { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
      { path: '/my-business', icon: Building2, label: 'My Business' },
      { path: '/projects', icon: Boxes, label: 'Projects', count: problemCount ?? undefined },
      { path: '/my-tools', icon: Wrench, label: 'My Tools' },
    ];

    return clientItems;
  };

  // Model A (one person = one kind): AA's operators are STAFF and live on the
  // staff shell (the launchpad at /expert), NOT on the client shell. So there is
  // no provider section here — the old is_provider-on-the-client-shell link was
  // a thin, worse copy of the builder's own all-projects/expert views and has
  // been retired. Staff reach the real screens by crossing into the builder.
  // The one exit point, so every role's nav goes through the permission filter.
  // Super and consultant navs are platform chrome, not company surfaces: their
  // items carry no perm, so the filter passes them through untouched.
  const getNavigationItems = (): NavItem[] => withPermissions(getBaseNavigationItems(), can);

  /*
   * The breadcrumb's second crumb, and the palette's destination list.
   *
   * Both read the same navigation the sidebar renders, so neither can name a
   * screen this person's role does not have. Home is always the first crumb,
   * which is why it is excluded from being the second one.
   */
  const navItems = getNavigationItems();

  const crumbLabel = (() => {
    if (location.pathname.startsWith('/company-context')) return contextLabel;
    if (location.pathname.startsWith('/registry')) return 'Use Case Registry';
    if (location.pathname.startsWith('/vendors')) return 'Vendor evaluations';
    // Longest path first: every cockpit page lives under /aai, so matching in
    // sidebar order named all of them "AI Adoption".
    const here = [...navItems]
      .filter((item) => item.path !== '/dashboard' && isActive(item.path))
      .sort((a, b) => b.path.length - a.path.length)[0];
    if (here) return here.label;
    return null;
  })();

  /*
   * The palette's groups. Pages come from the same navigation the sidebar
   * renders, which is what makes the palette's footer true: it cannot offer a
   * screen this role has no permission for. Actions are the things that are
   * not a place — they run where you are.
   */
  const pageDesc = (item: NavItem): string => {
    if (item.path === '/dashboard') return 'Return to your home';
    return `Open ${item.label}`;
  };

  const pages: Destination[] = navItems
    .filter((item) => !item.disabled)
    .map((item) => ({
      path: item.path,
      label: item.label,
      desc: pageDesc(item),
      current: isActive(item.path),
    }));

  if (contextInNav) {
    pages.push({
      path: '/company-context',
      label: contextLabel,
      desc: 'Approved Context, sources, people, and activity',
      current: isActive('/company-context'),
    });
  }

  const actions: Destination[] = [];
  if (canStartScoping) {
    actions.push({
      path: '/scope',
      label: 'Start scoping',
      desc: 'From code, a kind of build, or a problem',
    });
  }
  if (contextInNav) {
    actions.push({
      path: '/company-context?tab=activity',
      label: 'Open Activity',
      desc: 'See the permission-filtered operating record',
    });
  }
  actions.push({
    label: 'Account and settings',
    desc: 'Profile, access, connections, policy, and notifications',
    run: () => openSettings('general'),
  });

  const destGroups: DestGroup[] = [{ key: 'pages', label: 'Pages', items: pages }];
  if (actions.length > 0) destGroups.push({ key: 'actions', label: 'Actions', items: actions });

  const handleLogout = () => logoutUser('/login');
  // The Owner's People & departments is a screen (/team), not a Settings
  // section, so a request to open it goes to the screen.
  const sectionPage = useCallback((section?: string): string | null => {
    if (section === 'people' && (isOwner || isChampion)) return '/team';
    if (!isOwner) return null;
    if (section === 'connectors' || section === 'apps') return '/connectors';
    if (section === 'mcp') return '/connectors?tab=mcp';
    if (section === 'payment' || section === 'billing') return '/billing';
    return null;
  }, [isOwner, isChampion]);

  const openSettings = (section: string = 'general') => {
    setProfileMenuOpen(false);
    const page = sectionPage(section);
    if (page) {
      navigate(page);
      return;
    }
    setSettingsSection(section);
    setSettingsOpen(true);
  };

  // A page can ask the shell to open Settings on a section (Home's Plan
  // controls opens Plan and usage) without owning the modal itself.
  useEffect(() => {
    const onOpen = (event: Event) => {
      const section = ((event as CustomEvent).detail || {}).section as string | undefined;
      setProfileMenuOpen(false);
      const page = sectionPage(section);
      if (page) {
        navigate(page);
        return;
      }
      setSettingsSection(section || 'general');
      setSettingsOpen(true);
    };
    const onClose = () => setSettingsOpen(false);
    window.addEventListener('settings:open', onOpen);
    window.addEventListener('settings:close', onClose);
    return () => {
      window.removeEventListener('settings:open', onOpen);
      window.removeEventListener('settings:close', onClose);
    };
  }, [sectionPage, navigate]);

  let drawerState = '-translate-x-full';
  if (navOpen) drawerState = 'translate-x-0 shadow-[0_12px_32px_rgba(15,23,42,0.18)]';

  return (
    <div className="min-h-screen bg-[#F8F9FB] font-sans">
      {/*
        The shell. One element, two shapes, no state between them.

        Wide (640px and up): a fixed 224px column down the left, carrying its
        own 21px/14px/14px padding. Every child is simply an item in it — the
        wordmark included — so nothing inside restates the gutter and every
        row is the 196px the box leaves. It does not collapse and it
        has no toggle on wide screens.

        Narrow (below 640px): the same column, off screen, opened as a drawer
        from the menu button in the topbar (BLK-129). It used to unpin into a
        strip above the topbar, which stacked three header bars on a phone
        before any content. The drawer keeps the labelled rows, so the nav reads
        the same at every width.
      */}
      {navOpen && (
        <div aria-hidden className="fixed inset-0 z-[110] bg-black/30 sm:hidden" onClick={() => setNavOpen(false)} />
      )}
      <aside className={clsx(
        'sidebar fixed left-0 top-0 z-[120] flex h-full w-[280px] max-w-[85vw] flex-col overflow-y-auto border-r border-sidebar-border bg-white px-3.5 pb-3.5 pt-[21px] text-sidebar-text transition-transform duration-200 sm:z-[100] sm:bg-sidebar-bg sm:w-[224px] sm:max-w-none sm:translate-x-0',
        drawerState,
      )}>
        {/* The wordmark is an item in the sidebar like any other row: the
            aside's own padding places it, and it carries 8px of its own above
            and below so the hover fill has somewhere to sit.

            It is a link because it has a hover state, and hover feedback on
            something inert is a promise the interface does not keep. Home is
            where a wordmark goes. */}
        <Link
          to={isParticipant ? '/dashboard' : '/'}
          aria-label="Agents Anywhere — home"
          className="block shrink-0 rounded-act py-2 transition-colors hover:bg-sidebar-hover"
        >
          <img
            src="/brand/aa-logo-vector.png"
            alt="Agents Anywhere"
            className="brand-wordmark h-auto w-[151px] object-contain"
          />
        </Link>

        {canStartScoping && (
          <Link
            to="/scope"
            className={clsx(
              'mt-3 flex h-[42px] shrink-0 items-center justify-center gap-2 rounded-act px-[11px] text-[13px] font-semibold transition-colors',
              isActive('/scope') && 'bg-ink/85 text-white',
              !isActive('/scope') && 'bg-ink text-white hover:bg-ink/85',
            )}
          >
            <Plus size={16} strokeWidth={2.25} className="shrink-0" />
            Start scoping
          </Link>
        )}

        {/* Workspace panel was here — removed per the Phase 2 IA simplification.
            The "team / consultant / Open in Builder" content lives in the
            unified MyBusinessHome page now (under Overview lane card +
            forthcoming Team tab). Keeping it in the sidebar duplicated
            information and stole attention from primary nav. */}

        {/*
          Nav and the actions under it scroll together on wide screens, so a
          long admin nav never pushes Company Context off the bottom.
        */}
        <div className="flex flex-1 flex-col">
        {/*
          Wide: a stack of labelled rows. Narrow: evenly spread icon tabs, so
          the strip holds the whole nav on one line at any phone width.
        */}
        <nav className="block">
          {(() => {
            const items = getNavigationItems();
            // Awaiting-approval badge on the caller's dashboard nav item (F2 P0).
            if (approvalsPath && approvalsCount) {
              const dash = items.find((i) => i.path === approvalsPath);
              if (dash) dash.count = approvalsCount;
            }
            // Notifications are NOT a sidebar tab in the design — they live in
            // the topbar bell (see the <header> in <main>). Kept off the nav.
            return items;
          })().map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);

            // Group header ("Workspace" / "AI Adoption" / "Administration").
            // The icon-tab strip has no room for section labels, so they are
            // a wide-screen affordance only.
            const header = item.section ? (
              <p className="m-0 mt-[22px] mb-2 block text-[11px] font-semibold uppercase tracking-[0.14em] text-sidebar-heading">
                {item.section}
              </p>
            ) : null;

            const count = item.count;
            const hasCount = typeof count === 'number' && count > 0;

            const body = item.disabled ? (
              <div
                className="mb-2 flex h-[42px] cursor-not-allowed items-center gap-3 rounded-act px-[11px] text-[13px] font-medium text-sidebar-text/45"
                title="Coming soon"
              >
                <Icon size={18} strokeWidth={1.75} className="shrink-0" />
                <span className="flex-1">{item.label}</span>
                <span className="ml-auto text-[9px] font-semibold uppercase tracking-wide text-sidebar-text/50">soon</span>
              </div>
            ) : (
              <Link
                to={item.path}
                className={clsx(
                  // The active row is a filled pill carrying a black bar: on a
                  // light sidebar a fill alone is too quiet to find at a
                  // glance. The bar runs down the sidebar's edge when the nav
                  // is a column and under the tab when it is a row — same
                  // signal, turned to face the axis it is read along.
                  'aa-navitem relative mb-2 flex h-[42px] items-center gap-3 rounded-act px-[11px] text-[13px] transition-colors',
                  active
                    ? 'bg-sidebar-active text-sidebar-text-active font-semibold'
                    : 'font-medium text-sidebar-text hover:bg-sidebar-hover hover:text-sidebar-text-active',
                )}
                title={item.label}
              >
                {active && (
                  <span
                    aria-hidden
                    className="absolute bottom-1.5 left-[-8px] top-1.5 w-[3px] rounded-r bg-ink"
                  />
                )}
                <Icon size={18} strokeWidth={active ? 2 : 1.75} className="shrink-0" />
                <span className="flex-1">{item.label}</span>
                {hasCount && (
                  <span className={clsx(
                    'ml-auto grid h-[21px] min-w-[21px] place-items-center rounded-full px-2 font-sans text-[11px] tabular-nums',
                    active ? 'bg-accent-500 text-black' : 'bg-sidebar-hover text-sidebar-text',
                  )}>{count}</span>
                )}
                {hasCount && <span className="sr-only">{`${count} awaiting`}</span>}
              </Link>
            );

            return <React.Fragment key={item.path}>{header}{body}</React.Fragment>;
          })}

        </nav>

        {/* The Ask Orbit card is gone: the chrome names places, and Company
            Context is the one that holds what the card was promising. Asking
            still starts from the screen that showed you the gap, so every
            "Ask Orbit" action on Home, Projects and Results is untouched, and
            /orbit/request still resolves. */}
        </div>

        {/* Marketplace — pinned above profile for a plain client account, which
            is the only one whose nav includes it. Owners and champions reach it
            from the account menu instead. Keyed on having no workspace role
            rather than on not being four of them: a trainer and an expert were
            shown this link and then bounced off the route by the role guard. */}
        {(userRole === 'client' || userRole === 'client_admin') && !aaiRole && (
          <div className="px-2 pb-1">
            <Link
              to="/marketplace"
              className={clsx(
                'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-[13px] font-medium',
                isActive('/marketplace')
                  ? 'bg-sidebar-active text-sidebar-text-active font-semibold'
                  : 'text-sidebar-text hover:bg-sidebar-hover hover:text-sidebar-text-active',
              )}
              title="Marketplace"
            >
              <Store size={20} strokeWidth={isActive('/marketplace') ? 2 : 1.5} className="shrink-0" />
              <span>Marketplace</span>
            </Link>
          </div>
        )}

        {/*
          Builder: crosses into the builder in a new tab, with no builder login.

          It is the EXPERT's row. It was off entirely on the reading that Open
          Workspace was the one door for everybody, which is true of everybody
          who is not building: an expert's work IS the raw builder, and sending
          them through the workspace to reach it is a detour past the thing
          they opened the app for.

          The gate is the is_expert or is_builder_admin flag. Not `isProvider`,
          which is every one of the provider's people including those who never
          build: the flags are what the toggles set, and a person without either
          has nothing to do in there.
        */}
        {(isBuilderExpert || isBuilderAdmin) && (
          <div className="px-2 pb-1">
            <button
              onClick={openBuilder}
              disabled={openingBuilder}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-[13px] font-medium text-sidebar-text hover:bg-sidebar-hover hover:text-sidebar-text-active disabled:opacity-60"
              title="Builder"
            >
              <Boxes size={20} strokeWidth={1.5} className="shrink-0" />
              <span className="flex-1 text-left">{openingBuilder ? 'Opening…' : 'Builder'}</span>
            </button>
          </div>
        )}

        {/*
          The foot of the shell.

          Wide: Company Context as its own row, then the account row under a
          rule. Narrow: the design puts both on one line — Company Context
          takes the space and the avatar sits at the end. So they are one
          container that changes axis, not two that each guess where the other
          went. The unread count is not here: it belongs to the bell in the
          chrome, which every width shows.
        */}
        <div className="block">
        {contextInNav && (
          <Link
            to="/company-context"
            className={clsx(
              'relative flex h-[42px] min-w-0 items-center gap-3 rounded-act px-[10px] text-[13px] transition-colors',
              isActive('/company-context')
                ? 'bg-sidebar-active font-semibold text-sidebar-text-active'
                : 'font-medium text-sidebar-text hover:bg-sidebar-hover hover:text-sidebar-text-active',
            )}
            title={contextLabel}
          >
            {isActive('/company-context') && (
              <span aria-hidden className="absolute -left-2 top-1.5 bottom-1.5 w-[3px] rounded-r bg-ink" />
            )}
            <Database size={18} strokeWidth={1.75} className="shrink-0" />
            <span className="truncate">{contextLabel}</span>
          </Link>
        )}

        <div ref={profileMenuRef} className="relative mt-[14px] shrink-0">
          {profileMenuOpen && (
            <div
              className="absolute bottom-full left-0 right-0 mb-2 overflow-hidden rounded-xl border border-gray-200 bg-white text-gray-900 shadow-[0_12px_32px_rgba(15,23,42,0.18)]"
            >
              <div className="border-b border-gray-100 px-3 py-2.5">
                <div className="truncate text-[13px] font-semibold">{userName}</div>
                {userEmail && <div className="truncate font-sans text-[11px] text-gray-500">{userEmail}</div>}
              </div>
              {isOwner && ownerPlan === undefined && (
                <div aria-hidden className="flex items-center gap-2.5 border-b border-gray-100 px-3 py-2.5">
                  <CreditCard size={15} className="shrink-0 text-gray-300" />
                  <span className="min-w-0 flex-1 space-y-1.5 py-0.5">
                    <span className="block h-3 w-20 animate-pulse rounded bg-gray-100" />
                    <span className="block h-2.5 w-32 animate-pulse rounded bg-gray-100" />
                  </span>
                </div>
              )}
              {isOwner && ownerPlan?.billed && ownerPlan.can_manage && (
                <button
                  type="button"
                  onClick={() => openSettings('payment')}
                  className="flex w-full items-center gap-2.5 border-b border-gray-100 px-3 py-2.5 text-left transition-colors hover:bg-gray-50"
                >
                  <CreditCard size={15} className="shrink-0 text-gray-400" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[12.5px] font-semibold text-gray-900">{planTitle(ownerPlan)}</span>
                    <span className="block truncate text-[11px] text-gray-500">{planDetail(ownerPlan)}</span>
                  </span>
                </button>
              )}
              <div className="py-1">
                {(isOwner || isChampion) && (
                  <Link
                    to="/marketplace"
                    onClick={() => setProfileMenuOpen(false)}
                    className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-[12.5px] text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-950"
                  >
                    <Store size={15} className="text-gray-400" />
                    Marketplace
                  </Link>
                )}
                <button
                  type="button"
                  onClick={() => openSettings('general')}
                  className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-[12.5px] text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-950"
                >
                  <Settings size={15} className="text-gray-400" />
                  Settings
                </button>
              </div>
              <div className="border-t border-gray-100 py-1">
                <button
                  type="button"
                  onClick={() => { setProfileMenuOpen(false); handleLogout(); }}
                  className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-[12.5px] text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-950"
                >
                  <LogOut size={15} className="text-gray-400" />
                  Sign out
                </button>
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={() => setProfileMenuOpen((open) => !open)}
            className={clsx(
              'flex h-[45px] w-full items-start gap-3 rounded-lg px-[9px] pt-[10px] text-left transition-colors hover:bg-sidebar-hover',
              profileMenuOpen && 'bg-sidebar-active',
            )}
            title={`${userName} — Account`}
          >
            {profile?.profile_image ? (
              <img
                src={profile.profile_image.startsWith('http')
                  ? profile.profile_image
                  : `${process.env.REACT_APP_API_URL}/${profile.profile_image}`}
                alt="Profile"
                className="h-[34px] w-[34px] shrink-0 rounded-tile object-cover"
                onError={(e: any) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.style.display = 'none';
                }}
              />
            ) : (
              <AccountMark participant={isParticipant} name={userName} />
            )}
            {/* The name, the address, and the disclosure arrow are the wide
                shape only: on the strip the avatar is the whole control, and
                the menu it opens still names the person at the top. */}
            <span className="block min-w-0 flex-1">
              <span className="block truncate text-[13px] font-semibold text-sidebar-text">{userName}</span>
              <span className="block truncate text-[11px] text-sidebar-text">
                {ROLE_LABEL[aaiRole || ''] || userRole || 'Member'}
              </span>
            </span>
            <MoreHorizontal size={18} className="shrink-0 text-sidebar-text" />
          </button>
        </div>
        </div>
      </aside>

      {/* Main content area */}
      <main className="min-h-screen bg-canvas sm:ml-[224px]">
        {/* Sticky chrome bar — the switcher and Exit-impersonation live IN the
            chrome, so page content flows below and nothing is covered. It names
            the company on every tab: the screen already carries its own title
            in the page head, and repeating it here said the same thing twice
            while never answering "whose workspace am I in".
            On phones the menu button opens the sidebar as a drawer. */}
        <header className="topbar sticky top-0 z-[80] flex h-16 shrink-0 items-center gap-2 border-b border-line bg-[#FFFFFFE0] px-4 backdrop-blur sm:gap-3.5 sm:px-[30px]">
          <button
            type="button"
            onClick={() => setNavOpen(true)}
            aria-label="Open menu"
            aria-expanded={navOpen}
            className="inline-flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-act border border-line bg-white text-ink-control transition-colors hover:bg-canvas-inset sm:hidden"
          >
            <Menu size={17} />
          </button>
          {/* Company mark and breadcrumb hidden for now.
          {isAaiUser && (
            <div className="flex min-w-0 items-center gap-3">
              { /* The mark opens Company Context for the roles that have one, and
                  names the company for the roles that do not. * /}
              <CompanyMark to={contextInNav} name={clientName} />
              { /* A glyph, not an icon — 11px, so it sits on the breadcrumb's
                  baseline rather than on the company mark's. * /}
              <span aria-hidden className="hidden shrink-0 text-[11px] leading-[14.5px] text-[#92959D] lg:block">&rarr;</span>
              { /* The breadcrumb is the first thing to go as the header narrows.
                  The page's own H1 already says which screen this is; the
                  company name answers the question nothing else on the page
                  does, so it survives longer. * /}
              <nav aria-label="Breadcrumb" className="hidden min-w-0 items-center gap-1.5 lg:flex">
                <Link to="/dashboard" className="shrink-0 py-1 text-[11px] leading-[14.5px] text-ink-muted transition-colors hover:text-ink">
                  Home
                </Link>
                {crumbLabel && (
                  <>
                    <span aria-hidden className="shrink-0 text-[11px] text-ink-soft">/</span>
                    <span aria-current="page" className="truncate py-1 text-[11px] font-semibold leading-[14.5px] text-ink">
                      {crumbLabel}
                    </span>
                  </>
                )}
              </nav>
            </div>
          )}
          */}
          <div className="flex-1" />
          {/* The Workspace is for the people who BUILD, which today means the
              provider's own staff. It was open to everyone on the reading that
              the Workspace is where a person does the work; for a customer it
              is not. Their work arrives as an opportunity, a quote and a
              delivered agent, and the room where it gets built is ours.

              The gate is `is_provider`, so it follows the roster rather than a
              hardcoded name: promoting another company to a provider opens it
              to their staff and nobody else's.

              This is NOT the Builder row above: that stays flagged, because
              Builder sits underneath the Workspace and a person crosses into
              it only when the work actually needs it. Opening the Workspace
              buys no seat. The crossing mints a single-use ticket and the
              builder creates the user on redeem, so the cost of this is a row,
              not a licence. */}
          {/* Every member of a client company, not only provider staff: the
              crossing checks membership itself, and without a project there
              was no other way into the Workspace. It renders nothing for a
              person with no company. */}
          <ContextSwitcher />
          {isImpersonating && (
            <button
              onClick={endImpersonation}
              title="Exit impersonation"
              aria-label="Exit impersonation"
              className="inline-flex h-[34px] shrink-0 items-center justify-center gap-1.5 rounded-full bg-accent-500 px-2.5 text-[12.5px] font-semibold text-gray-900 shadow-sm hover:brightness-95 transition-all sm:h-10 sm:px-4 whitespace-nowrap"
            >
              <X size={15} /> <span className="hidden sm:inline">Exit impersonation</span>
            </button>
          )}
          {isAaiUser && (
            <button
              type="button"
              onClick={() => setGoToOpen(true)}
              title="Go to (⌘K)"
              className="inline-flex h-[34px] w-[34px] shrink-0 items-center justify-center gap-2 rounded-act border border-line bg-white text-[12px] text-ink-control transition-colors hover:bg-canvas-inset sm:w-auto sm:px-[9px]"
            >
              <Search size={15} className="shrink-0" />
              <span className="hidden sm:inline">Go to</span>
              <kbd className="hidden shrink-0 rounded border border-line bg-white px-1.5 py-0.5 font-sans text-[10.5px] text-ink-soft sm:inline">⌘K</kbd>
            </button>
          )}
          {contextInNav && (
            <Link
              to="/company-context?tab=activity"
              title="Activity"
              className="inline-flex h-[34px] w-[34px] shrink-0 items-center justify-center gap-2 rounded-act border border-line bg-white text-[12px] font-semibold text-ink-control transition-colors hover:bg-canvas-inset sm:w-auto sm:px-[10px]"
            >
              <Clock size={15} className="shrink-0" />
              <span className="hidden sm:inline">Activity</span>
            </Link>
          )}
          {/* Activity is the record of what happened; the bell is what happened
              to you. Every role in this shell gets it. */}
          {isAaiUser && <NotificationBell scope="client" allHref="/notifications" />}
          {/* Scope a problem / Map business — HIDDEN FOR NOW (CTO 08-13).
              New-agent scoping enters through Orbit; the planner handles it
              onward. Re-enable by flipping this flag. */}
          {false && (
          <button
            onClick={openScope}
            title={isMyBusiness ? "Map your business" : "Scope a problem"}
            className="group inline-flex h-10 items-center gap-2.5 rounded-full border border-gray-200 bg-white pl-1.5 pr-4 shadow-sm hover:border-brand-200 hover:shadow transition-all"
          >
            <span className="grid place-items-center w-7 h-7 rounded-full bg-gray-900 shrink-0">
              <img src="/icon-white.png" alt="Orbit" className="w-4 h-4 object-contain" />
            </span>
            <span className="text-[13px] font-semibold text-gray-700 group-hover:text-brand-600 transition-colors">
              {isMyBusiness ? "Map your business" : "Scope a problem"}
            </span>
          </button>
          )}
        </header>

        {/* The portal canvas. Panels are white, so the canvas cannot be: a
            white card on a white page has no structure left to read.
            Measured from the rendered platform: the content box carries a 30px
            side margin and 40px / 80px of padding, and no side padding of its
            own. The generous bottom is deliberate, so the last card on a page
            never sits against the fold. The 1200px measure is PageBody's, not
            this box's, so a full-bleed screen can still opt out of it. */}
        <div className="page-content mx-5 pb-20 pt-8 sm:mx-[30px] sm:pt-10">
          {children}
        </div>
      </main>

      {/* Global Orbit drawer — the single drawer for most pages, including
          /problems and /problems/:id (those used to mount their own, which
          double-opened with this one; now they dispatch `orbit:open` with a
          problem context to summon THIS drawer). /my-business and /orbit still
          mount their own, so skip the global one there to avoid a double drawer. */}
      {!['/my-business', '/orbit'].some(p => location.pathname.startsWith(p)) && (
        <OrbitChatDrawer
          clientId={clientId || localStorage.getItem('client_id') || ''}
          onAfterTurn={() => window.dispatchEvent(new Event('orbit:after-turn'))}
          context="problem"
          freshProblem
          initialGreeting="What's draining your team? Tell me the problem and I'll scope it into a build."
        />
      )}

      {isAaiUser && <AiCompanion />}

      {goToOpen && (
        <GoToPalette
          groups={destGroups}
          roleLabel={ROLE_LABEL[aaiRole || ''] || userRole || 'this role'}
          onClose={() => setGoToOpen(false)}
        />
      )}

      <WorkspaceSettingsModal
        open={settingsOpen}
        section={settingsSection}
        onClose={() => setSettingsOpen(false)}
        onSection={setSettingsSection}
        clientId={clientId || localStorage.getItem('client_id') || ''}
        clientName={clientName}
        userName={userName}
        userEmail={userEmail}
        userRole={userRole}
        profile={profile}
        onLogout={handleLogout}
      />
    </div>
  );
};

export default Layout;
